"use client"

import { useMutation, useQueryClient } from "@tanstack/react-query"

import { shopifyTopupIncidentQueryKey } from "@/hooks/queries/shopify-topup-incidents"
import { useTranslatedQueryHandling } from "@/hooks/use-translated-query-handling"

import {
  clearShopifyTopupBlockAction,
  resolveShopifyTopupAttemptAction,
} from "@/server-actions/shopify/topup-admin"
import { useTranslations } from "next-intl"


type Resolution = "credited" | "failed"

export function useResolveShopifyTopupAttemptMutation(tenantId: string, onResolved: () => void) {
  const withQueryHandling = useTranslatedQueryHandling()

  const tUi = useTranslations("Feedback.MutationsShopifyTopupIncidents")

  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ attemptId, resolution }: { attemptId: string; resolution: Resolution }) => {
      const result = await withQueryHandling(
        resolveShopifyTopupAttemptAction({ tenantId, attemptId, resolution }),
        {
          loading: tUi("resolvingAttempt"),
          success: () =>
            resolution === "credited"
              ? "Wallet credited and the attempt closed"
              : "Attempt written off as never billed",
        },
      )
      if (!("ok" in result)) throw new Error(result.errorMessage)
      return result
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: shopifyTopupIncidentQueryKey(tenantId) })
      onResolved()
    },
  })
}

export function useClearShopifyTopupBlockMutation(tenantId: string, onCleared: () => void) {
  const withQueryHandling = useTranslatedQueryHandling()

  const tUi = useTranslations("Feedback.MutationsShopifyTopupIncidents")

  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async () => {
      const result = await withQueryHandling(clearShopifyTopupBlockAction({ tenantId }), {
        loading: tUi("liftingTheHold"),
        success: () => "Top-ups unblocked",
      })
      if (!("ok" in result)) throw new Error(result.errorMessage)
      return result
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: shopifyTopupIncidentQueryKey(tenantId) })
      onCleared()
    },
  })
}
